import React from 'react'
// c:\xampp\htdocs\miners-market-main\components\NotificationManager.js

export default function NotificationManager() {
    const [activeTab, setActiveTab] = React.useState('send'); // 'send' or 'history'
    const [title, setTitle] = React.useState('');
    const [body, setBody] = React.useState('');
    const [target, setTarget] = React.useState('all');
    const [url, setUrl] = React.useState('/equipment');
    const [users, setUsers] = React.useState([]);
    const [allUsers, setAllUsers] = React.useState([]);
    const [status, setStatus] = React.useState({ message: '', type: '' });
    const [isLoading, setIsLoading] = React.useState(false);
    const [history, setHistory] = React.useState([]);

    React.useEffect(() => {
        if (target === 'specific') {
            // Updated to use the correct admin endpoint
            fetch('/api/admin_get_all_users.php', { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    if (data.error) throw new Error(data.error);
                    setAllUsers(data);
                })
                .catch(err => {
                    console.error("Failed to fetch users:", err);
                    setStatus({ message: 'Failed to load user list.', type: 'error' });
                });
        }
    }, [target]);

    // Fetch history when tab changes
    React.useEffect(() => {
        if (activeTab === 'history') {
            fetch('/api/admin_get_notification_history.php', { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) setHistory(data);
                })
                .catch(err => console.error("Failed to fetch history:", err));
        }
    }, [activeTab]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStatus({ message: '', type: '' });

        try {
            const response = await fetch('/api/send_notification.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title, body, target, url, users })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to send notification.');
            }

            setStatus({ message: result.message || 'Notification sent successfully!', type: 'success' });
            // Clear form
            setTitle('');
            setBody('');
        } catch (error) {
            setStatus({ message: error.message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const predefinedUrls = [
        { label: 'Equipment Marketplace', value: '/equipment' },
        { label: 'Rebuilds', value: '/rebuilds' },
        { label: 'Consumables', value: '/consumables' },
        { label: 'Miscellaneous', value: '/miscellaneous' },
        { label: 'Rentals', value: '/rentals' },
        { label: 'Commercial', value: '/commercial' },
        { label: 'Components & Spares', value: '/components-and-spares' },
        { label: 'User Dashboard', value: '/dashboard' },
        { label: 'Home Page', value: '/' },
    ];

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
            <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Push Notification Manager</h2>
                <p className="text-gray-500 text-sm mt-1">Send real-time updates to your users' devices.</p>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 mb-6 border-b border-gray-200">
                <button 
                    onClick={() => setActiveTab('send')}
                    className={`pb-2 px-4 text-sm font-medium transition-colors ${activeTab === 'send' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Send Notification
                </button>
                <button 
                    onClick={() => setActiveTab('history')}
                    className={`pb-2 px-4 text-sm font-medium transition-colors ${activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    History
                </button>
            </div>

            {activeTab === 'send' ? (
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Column: Form */}
                <div className="flex-1">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Notification Title</label>
                            <input 
                                type="text" 
                                id="title" 
                                value={title} 
                                onChange={e => setTitle(e.target.value)} 
                                required 
                                placeholder="e.g., New Stock Alert!"
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                            />
                        </div>
                        
                        <div>
                            <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">Message Body</label>
                            <textarea 
                                id="body" 
                                value={body} 
                                onChange={e => setBody(e.target.value)} 
                                required 
                                rows="3" 
                                placeholder="e.g., Check out the latest excavators added to our inventory."
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            ></textarea>
                            <p className="text-xs text-gray-500 mt-1 text-right">{body.length} characters</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="target" className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                                <select 
                                    id="target" 
                                    value={target} 
                                    onChange={e => setTarget(e.target.value)} 
                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="all">All Users</option>
                                    <option value="buyers">All Buyers</option>
                                    <option value="sellers">All Sellers</option>
                                    <option value="specific">Specific Users</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
                                <select 
                                    id="url" 
                                    value={url} 
                                    onChange={e => setUrl(e.target.value)} 
                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    {predefinedUrls.map(item => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {target === 'specific' && (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <label htmlFor="users" className="block text-sm font-medium text-gray-700 mb-2">Select Users</label>
                                <select
                                    id="users"
                                    multiple
                                    value={users}
                                    onChange={e => setUsers(Array.from(e.target.selectedOptions, option => option.value))}
                                    className="w-full h-48 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                >
                                    {allUsers.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.email}) - {user.roles}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-2">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    Hold <strong>Ctrl</strong> (Windows) or <strong>Cmd</strong> (Mac) to select multiple users.
                                </p>
                            </div>
                        )}

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 transition-colors"
                            >
                                {isLoading ? (
                                    <><i className="fas fa-spinner fa-spin mr-2"></i> Sending...</>
                                ) : (
                                    <><i className="fas fa-paper-plane mr-2"></i> Send Notification</>
                                )}
                            </button>
                        </div>
                    </form>

                    {status.message && (
                        <div className={`mt-4 p-4 rounded-lg flex items-center ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                            <i className={`fas ${status.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-3 text-lg`}></i>
                            <span className="text-sm font-medium">{status.message}</span>
                        </div>
                    )}
                </div>

                {/* Right Column: Live Preview */}
                <div className="w-full lg:w-1/3">
                    <div className="sticky top-6">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Live Preview</h3>
                        
                        {/* Android/Chrome Style Notification Preview */}
                        <div className="bg-gray-100 p-6 rounded-xl border border-gray-200 shadow-inner flex justify-center items-center min-h-[200px]">
                            <div className="bg-white w-full max-w-sm rounded-lg shadow-md overflow-hidden border border-gray-100">
                                <div className="p-3 flex items-start space-x-3">
                                    <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white">
                                            <i className="fas fa-hard-hat"></i>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-baseline">
                                            <p className="text-xs font-medium text-gray-900">Miners Market</p>
                                            <span className="text-[10px] text-gray-400">now</span>
                                        </div>
                                        <p className="text-sm font-semibold text-gray-800 truncate mt-0.5">
                                            {title || 'Notification Title'}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                                            {body || 'The body of your notification will appear here. It gives users more context about the alert.'}
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-3 py-2 border-t border-gray-100 flex justify-end">
                                    <div className="h-2 w-16 bg-gray-200 rounded"></div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-100">
                            <h4 className="text-sm font-semibold text-blue-800 mb-2">Target Summary</h4>
                            <ul className="text-sm text-blue-700 space-y-1">
                                <li><span className="font-medium">Audience:</span> {target === 'all' ? 'Everyone' : target.charAt(0).toUpperCase() + target.slice(1)}</li>
                                {target === 'specific' && <li><span className="font-medium">Selected:</span> {users.length} user(s)</li>}
                                <li><span className="font-medium">Link:</span> {predefinedUrls.find(u => u.value === url)?.label || url}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            ) : (
                /* History Tab Content */
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stats</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {history.length === 0 ? (
                                <tr><td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No notifications sent yet.</td></tr>
                            ) : (
                                history.map(item => (
                                    <tr key={item.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(item.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            <div className="font-medium">{item.title}</div>
                                            <div className="text-gray-500 truncate max-w-xs">{item.body}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                                            {item.url}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs mr-2">Sent: {item.sent_count}</span>
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">Read: {item.read_count}</span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
